import {tx, ptx, usePageText} from '../lib/page-text';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { CopyIcon, CheckIcon, ExternalIcon } from '../components/icons';

interface Clip {
  id: string;
  recordingId: string;
  title: string | null;
  description: string | null;
  startSec: number;
  endSec: number;
  assetUrl: string | null;
  posterUrl: string | null;
  isPublic: boolean;
  status: 'pending' | 'rendering' | 'ready' | 'failed';
  errorMessage: string | null;
  createdAt: string;
  renderedAt: string | null;
}

const STATUS_STYLE: Record<Clip['status'], { bg: string; color: string; label: string }> = {
  pending:   { bg: 'rgba(0,210,255,.12)',  color: '#5ad6ff',         label: 'Queued' },
  rendering: { bg: 'rgba(245,165,36,.14)', color: '#ffce6b',         label: 'Rendering' },
  ready:     { bg: 'rgba(33,214,114,.14)', color: '#5fe69c',         label: 'Ready' },
  failed:    { bg: 'rgba(239,68,68,.14)',  color: '#ff8888',         label: 'Failed' },
};

export function ClipsPage() {
  usePageText();
  const list = useQuery({
    queryKey: ['clips'],
    queryFn: async () => (await api.get('/me/clips')).data as Clip[],
    refetchInterval: 10_000,
  });

  return (
    <div>
      <div className="row-between" style={{ marginBottom: 8 }}>
        <div>
          <h1 className="h1">{tx("Clips")}</h1>
          <p className="subtitle">{tx("Cut a window from any finished recording and share it as a hosted page. Powers TikTok-style short shares, \"best moments\" feeds, and embedded highlight reels.")}</p>
        </div>
      </div>

      {list.isLoading && <div className="muted">{tx("Loading…")}</div>}
      {list.isError && <div className="error">{tx("Failed to load clips.")}</div>}

      {list.data && list.data.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <h3 style={{ marginBottom: 8 }}>{tx("No clips yet")}</h3>
          <p className="muted" style={{ marginBottom: 8, maxWidth: 500, marginLeft: 'auto', marginRight: 'auto' }}>{ptx("Clips are cut from recordings. Open a stream → recordings tab → pick an in/out point, or call {v0} with{v1}{v2}.", {v0: (<code style={{ fontSize: 12 }}>POST /v1/me/clips</code>), v1: (' '), v2: (<code style={{ fontSize: 12 }}>{'{ recordingId, startSec, endSec }'}</code>)})}</p>
          <p className="muted" style={{ fontSize: 12, maxWidth: 500, marginLeft: 'auto', marginRight: 'auto' }}>{ptx("Max clip duration: 10 minutes. Output: H.264 MP4 + JPEG poster.{v0}{v1}", {v0: (' '), v1: (<a href="https://kardocloud.com/Live/Developer/clips.html" target="_blank" rel="noopener" style={{ color: 'var(--primary)' }}>{tx("Read the docs →")}</a>)})}</p>
        </div>
      )}

      {list.data && list.data.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
          {list.data.map((c) => <ClipCard key={c.id} clip={c}/>)}
        </div>
      )}
    </div>
  );
}

function ClipCard({ clip }: { clip: Clip }) {
  usePageText();
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const style = STATUS_STYLE[clip.status];
  const duration = Math.round((clip.endSec - clip.startSec) * 10) / 10;

  const del = useMutation({
    mutationFn: async () => (await api.delete(`/me/clips/${clip.id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clips'] }),
  });

  const shareUrl = `https://kardocloud.com/clips/${clip.id}`;
  const copyShare = () => {
    navigator.clipboard?.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        aspectRatio: '16/9', background: '#000',
        position: 'relative', display: 'grid', placeItems: 'center',
        backgroundImage: clip.posterUrl ? `url(${clip.posterUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}>
        {!clip.posterUrl && <div className="muted" style={{ fontSize: 13 }}>{tx(style.label)}</div>}
        <span style={{
          position: 'absolute', top: 8, right: 8,
          fontSize: 10, padding: '3px 9px', borderRadius: 999,
          background: style.bg, color: style.color, fontWeight: 700,
          letterSpacing: 0.5,
        }}>
          {tx(style.label)}
        </span>
        <span style={{
          position: 'absolute', bottom: 8, right: 8,
          fontSize: 11, padding: '3px 8px', borderRadius: 4,
          background: 'rgba(0,0,0,0.7)', color: '#fff', fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {duration}s
        </span>
      </div>
      <div style={{ padding: 14, flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>
          {clip.title || ptx("Clip · {v0}s window", {v0: (duration)})}
        </div>
        {clip.description && (
          <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.4 }}>{clip.description}</div>
        )}
        {clip.status === 'failed' && clip.errorMessage && (
          <div className="error" style={{ fontSize: 11.5 }}>{clip.errorMessage}</div>
        )}
        <div className="muted" style={{ fontSize: 11, marginTop: 'auto' }}>
          {clip.renderedAt
            ? ptx("Rendered {v0}", {v0: (new Date(clip.renderedAt).toLocaleString())})
            : ptx("Created {v0}", {v0: (new Date(clip.createdAt).toLocaleString())})}
        </div>
        <div className="flex gap" style={{ marginTop: 4 }}>
          {clip.status === 'ready' && clip.assetUrl && (
            <>
              <a className="btn sm" href={clip.assetUrl} target="_blank" rel="noopener">{tx("Play ↗")}</a>
              <button className="btn outline sm" onClick={copyShare}>
                {copied ? <CheckIcon size={11}/> : <CopyIcon size={11}/>}
                {copied ? tx("Copied") : tx("Share")}
              </button>
            </>
          )}
          <button
            className="btn ghost sm"
            style={{ color: 'var(--danger)', marginLeft: 'auto' }}
            onClick={() => { if (confirm('Delete this clip + its asset?')) del.mutate(); }}
            disabled={del.isPending}
          >
            {del.isPending ? '…' : tx("Delete")}
          </button>
        </div>
      </div>
    </div>
  );
}
